import _ from 'lodash';
import type { FC, ReactNode } from 'react';
import React, {
  createContext,
  useEffect,
  useState
} from 'react';
import { Layout } from 'src/components/SimpleKeyboard';
import { THEMES } from 'src/constants';
import { confirmationCompleteTime } from 'src/utils/common';

interface TableSizes {
  [key: string]: number;
}
interface Tables {
  sizes: TableSizes;
}

export interface Settings {
  direction?: 'ltr' | 'rtl';
  responsiveFontSizes?: boolean;
  theme?: string;
  stationId?: number;
  pos?: string;
  permanentId?: string;
  completeTime?: number;
  manualStart?: boolean;
  rightDirectionArrow?: boolean;
  facialStart?: boolean;
  barcodeReaderStart?: boolean;
  emailReceiptEnabled?: boolean;
  paymentTerminalLocation?: 'right' | 'left' | 'top' | 'bottom';
  discountsEnabled?: boolean;
  alwaysPrintReceipt?: boolean;
  currencyType?: CurrencyType;
  hotButtonItemsEnabled?: boolean;
  hotButtonItemsText?: string;
}

export enum CurrencyType {
  USD = 'USD',
  GBP = 'GBP'
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

interface SettingsProviderProps {
  settings?: Settings;
  children?: ReactNode;
}

const defaultSettings: Settings = {
  direction: 'ltr',
  responsiveFontSizes: true,
  theme: THEMES.LIGHT,
  completeTime: confirmationCompleteTime.default,
  currencyType: CurrencyType.USD,
  paymentTerminalLocation: 'right'
};

export const restoreSettings = (): Settings | null => {
  let settings = null;

  try {
    const storedData: string | null = window.localStorage.getItem('settings');

    if (storedData) {
      settings = JSON.parse(storedData);
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

export const storeSettings = (settings: Settings): void => {
  window.localStorage.setItem('settings', JSON.stringify(settings));
};

const SettingsContext = createContext<SettingsContextValue>({
  settings: defaultSettings,
  saveSettings: () => { },
});

export const SettingsProvider: FC<SettingsProviderProps> = ({ settings, children }) => {
  const [currentSettings, setCurrentSettings] = useState<Settings>(settings || defaultSettings);
  const handleSaveSettings = (update: Settings = {}): void => {
    const mergedSettings = _.merge({}, currentSettings, update);

    setCurrentSettings(mergedSettings);
    storeSettings(mergedSettings);
  };

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setCurrentSettings(restoredSettings);
    }
  }, []);

  useEffect(() => {
    document.dir = currentSettings.direction;
  }, [currentSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings: currentSettings,
        saveSettings: handleSaveSettings
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
